import {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  AutocompleteValue,
  Box,
  CircularProgress,
  createFilterOptions,
  FilterOptionsState,
  LinearProgress,
} from '@mui/material';
import MuiAutoComplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useUNSAFE_PortalContext } from '@react-aria/overlays';
import { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import UnderlinedMatch from './UnderlinedMatch';

export type ExtraAutoCompleteProps = {
  label?: string;
  freeSoloPrefix?: string;
  textFieldProps?: React.ComponentProps<typeof TextField>;
  components?: { TextField?: React.ComponentType<React.ComponentProps<typeof TextField>> };
  details?: (option, inputValue: string) => React.ReactNode;
  getOptionKey?: (option) => string;
  error?: boolean;
  showResultsList?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  transform?: (value) => AutocompleteValue;
  border?: string | number;
  width?: string | number;
  // this is stupid
  preventOpen?: boolean;
  autoExpand?: boolean;
  hasFocus?: boolean;
  defaultInputValue?: string;
  commitPendingOnBlur?: boolean;
};

export type AutocompleteWithoutRender<
  T,
  Multiple extends boolean,
  DisableClearable extends boolean,
  FreeSolo extends boolean,
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> & {
  renderInput?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderInput'];
  suggestedValue?: T;
};

export default function Autocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  label,
  getOptionLabel,
  getOptionKey = getOptionLabel,
  textFieldProps,
  components,
  freeSoloPrefix = 'Add New',
  details,
  onChange,
  error,
  multiple,
  loading,
  value,
  showResultsList = true,
  startAdornment = null,
  endAdornment = null,
  disabled,
  open: controlledOpen,
  preventOpen,
  transform = (value) => value,
  onOpen,
  onClose,
  onKeyDown,
  border,
  wrapperProps = {},
  autoExpand = true,
  onFocus,
  onBlur,
  width = '100%',
  clearOnEscape = true,
  sx = {},
  renderInput,
  hasFocus,
  suggestedValue,
  defaultInputValue,
  commitPendingOnBlur = false,
  onInputChange,
  ...props
}: AutocompleteWithoutRender<T, Multiple, DisableClearable, FreeSolo> & ExtraAutoCompleteProps) {
  const TextFieldComponent = components?.TextField || TextField;
  type ValueType = AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  const [highlightedOption, setHighlightedOption] = useState<ValueType>(null);
  const [inputValue, setInputValue] = useState(defaultInputValue || '');
  /**
   * Is the text in the box the user's own FILTER, or MUI reading a chosen value back?
   * MUI reports `reason: 'input'` only for typing; every `'reset'` / `'clear'` is the
   * component syncing the box to the value. Only a live filter has a pending pick.
   */
  const [inputIsUserFilter, setInputIsUserFilter] = useState(false);
  /**
   * MUI fires `onHighlightChange` only for 'mouse' / 'keyboard' / 'touch', never for its own `autoHighlight` re-derive, so the callback firing IS the signal that a person chose this option.
   * A REF, not state: this editor re-renders constantly and is re-parented, and as state the intent was raced away between the arrow and the Tab.
   */
  const userChosenHighlight = useRef<ValueType | null>(null);
  const [open, setOpen] = useState(false);
  // When inside an AgModal (react-aria), portal the popper into the modal's subtree
  // so it stays inside the focus trap. Outside a modal, getContainer is undefined
  // and MUI uses its default (document.body).
  const { getContainer } = useUNSAFE_PortalContext();
  const popperSlotProps = useMemo(
    () =>
      ({ ...(getContainer && { container: getContainer() }), ...props.slotProps?.popper }) as NonNullable<
        typeof props.slotProps
      >['popper'],
    [getContainer, props.slotProps?.popper]
  );
  const mergedSlotProps = useMemo(
    () => ({ ...props.slotProps, popper: popperSlotProps }),
    [props.slotProps, popperSlotProps]
  );

  /** Exactly what MUI applies when a caller passes no `filterOptions` of its own. */
  const resolvedFilterOptions = useMemo(() => props.filterOptions ?? createFilterOptions<T>(), [props.filterOptions]);

  const activeFilterOptions = useCallback(
    (options: T[], state: FilterOptionsState<T>) =>
      resolvedFilterOptions(options, { ...state, inputValue: inputIsUserFilter ? state.inputValue : '' }),
    [resolvedFilterOptions, inputIsUserFilter]
  );

  const renderOption = (
    innerProps: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
    ownerState: AutocompleteOwnerState<T, Multiple, DisableClearable, FreeSolo>
  ) => {
    const isFreeSoloOption =
      typeof option === 'object' &&
      Object.hasOwn(option, 'freeSolo') &&
      (option as { freeSolo?: string } & unknown).freeSolo;
    const showPrefix = isFreeSoloOption && freeSoloPrefix?.length > 0;
    const label = getOptionLabel(option);

    return (
      <li {...innerProps} key={`${getOptionKey(option)}-${state.index}`}>
        {props.renderOption ? (
          props.renderOption(innerProps, option, state, ownerState)
        ) : (
          <div>
            {showPrefix && <span>{`${freeSoloPrefix} "`}</span>}
            <UnderlinedMatch target={label} input={state.inputValue} />
            {showPrefix && <span>"</span>}
            {details && details(option, state.inputValue)}
          </div>
        )}
      </li>
    );
  };

  // this shit suckkkkkssssssssssssssssssssssssssssssss
  const isOpen = controlledOpen || (open && !preventOpen);
  const [focused, setFocused] = useState(false);

  /** ENTERING the cell opens the listbox, leaving it closes it — driven by the grid's `hasFocus`, which flips ONCE per entry, NOT by DOM focus (MUI's `openOnFocus` re-fires on the portal-repair refocus and reopens a listbox dismissed with Escape). */
  useEffect(() => {
    if (hasFocus) {
      setOpen(true);
      return;
    }

    setFocused(false);
    setOpen(false);
    userChosenHighlight.current = null;
  }, [hasFocus]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const callerInputRef = textFieldProps?.inputRef;
  const mergedInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof callerInputRef === 'function') {
        callerInputRef(node);
      } else if (callerInputRef) {
        (callerInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [callerInputRef]
  );

  /**
   * A dropdown cell is entered with the caret at the END of the value read back into
   * the box, so the first keystroke APPENDS ('Marketing & Promotion' + 'Fre') instead
   * of filtering. Selecting that read-back makes the keystroke REPLACE it.
   *
   *   box holds a value read-back ──▶ select it, typing filters   ✓
   *   box holds the user's filter ──▶ leave it, they are mid-word ✓
   *
   * Keyed on `inputValue` because the label lands a beat AFTER the focus that
   * entered the cell, and it re-runs after the re-parent blur re-reads the value.
   *
   * `hasFocus` is the grid's own answer to "which cell is being edited", and only
   * one cell can hold it. DOM focus is NOT checked: it is still on <body> in the
   * commit where the label lands, and a later `focus()` keeps a selection already
   * set on the input.
   */
  useEffect(() => {
    if (!hasFocus || inputIsUserFilter || !inputValue) return;

    inputRef.current?.select();
  }, [hasFocus, inputValue, inputIsUserFilter]);

  const [val, setVal] = useState(value ?? suggestedValue ?? null);

  useEffect(() => {
    if (!value && suggestedValue && !focused) {
      setVal(suggestedValue);
    } else {
      setVal(value);
    }
  }, [value, suggestedValue, focused]);

  /**
   * Sync highlightedOption with MUI's internal highlight state.
   *
   * MUI v6 only fires onHighlightChange for 'mouse'/'keyboard'/'touch' reasons,
   * NOT when autoHighlight resets to index 0 after filtering. We must manually
   * sync whenever the filtered list changes (inputValue or options change).
   *
   * The filter has to be the SAME one MUI is applying, or this re-derive names an
   * option the listbox is not even showing. A caller that passes no `filterOptions`
   * still gets MUI's `createFilterOptions()` default downstream, so falling back to
   * the unfiltered array picked `options[0]` while the user was looking at the top
   * of a filtered list (TAS-3357).
   *
   * biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally syncing on inputValue and options.
   */
  useEffect(() => {
    if (!isOpen) return;

    const filteredOptions = activeFilterOptions([...props.options], { inputValue, getOptionLabel });

    /**
     * A pick the user made outlives this re-derive for as long as the listbox is
     * still OFFERING it — a rebuilt array of the same options keeps it, a genuinely
     * different list drops it.
     */
    if (userChosenHighlight.current !== null && !filteredOptions.includes(userChosenHighlight.current as T)) {
      userChosenHighlight.current = null;
    }

    if (userChosenHighlight.current !== null) return;

    if (filteredOptions.length > 0) {
      setHighlightedOption(filteredOptions[0] as ValueType);
    } else {
      setHighlightedOption(null);
    }
  }, [inputValue, props.options, isOpen, activeFilterOptions]);

  /**
   * The editor's PENDING value — what the user has chosen but not yet handed over.
   * `null` when there is nothing outstanding.
   *
   *   open listbox + typed filter + a live highlight ──▶ that option
   *   input already reads back the chosen value       ──▶ null   ◄── never clobber
   *   empty input / closed listbox / multi-select     ──▶ null
   *
   * `inputIsUserFilter` is why this is safe to run on every blur. Once a pick lands
   * (mouse click, Enter, MUI's own reset) MUI rewrites the box to that value's label
   * with `reason: 'reset'`; the highlight left behind is just `autoHighlight`
   * re-deriving a first pick, and committing it would OVERWRITE what the user chose.
   * An empty input carries the same arbitrary highlight and would clobber
   * `useCellPrediction`.
   *
   * Which option this returns is deliberately UNCHANGED from the Tab branch below —
   * this only decides WHETHER the pending pick is handed over, never WHICH one.
   */
  const pendingHighlightedOption = (): ValueType | null => {
    if (multiple || !isOpen || !highlightedOption) {
      return null;
    }

    if (!(inputValue && inputIsUserFilter) && userChosenHighlight.current === null) {
      return null;
    }

    // Same identity check the Tab branch makes: a highlight left over from a previous
    // options array is not committable.
    if (!props.options.some((option) => option === highlightedOption)) {
      return null;
    }

    return highlightedOption;
  };

  return (
    <Box
      {...wrapperProps}
      sx={[
        { width: width, position: 'relative', height: '100%' },
        ...(Array.isArray(wrapperProps.sx) ? wrapperProps.sx : [wrapperProps.sx]),
      ]}
    >
      <MuiAutoComplete
        {...props}
        slotProps={mergedSlotProps}
        multiple={multiple}
        value={val}
        loading={loading}
        sx={{
          ...(border !== undefined && {
            [`& .MuiOutlinedInput-root`]: {
              [`& fieldset`]: { border: border },
              [`&:hover fieldset`]: { border: border },
              [`&.Mui-focused fieldset`]: { border: border },
            },
          }),
          ...sx,
        }}
        disabled={disabled}
        filterOptions={activeFilterOptions}
        color="error.main"
        getOptionLabel={getOptionLabel}
        clearOnEscape={clearOnEscape}
        autoHighlight
        onFocus={(e) => {
          setFocused(true);

          // The re-parent blur/refocus leaves the caret collapsed at the end of the
          // value, so without this the NEXT keystroke appends to it again. Skipped
          // while the box holds a filter the user is still typing.
          if (hasFocus && !inputIsUserFilter) {
            (e.target as HTMLInputElement).select?.();
          }

          onFocus?.(e);
        }}
        onBlur={(e) => {
          // Resolved up front so the branch below is one decision, read off THIS render's
          // state — MUI's own blur handler has already run on the input by now and queued
          // its `reason: 'reset'` input change, which only lands on the next render.
          const pendingOption = commitPendingOnBlur ? pendingHighlightedOption() : null;

          if (!value && suggestedValue && inputValue === getOptionLabel(suggestedValue)) {
            const transformed =
              suggestedValue instanceof Array ? suggestedValue.map(transform) : transform(suggestedValue);
            onChange(e, transformed, 'blur-sm');
          } else if (pendingOption) {
            // FLUSH BEFORE DIFF: the grid's Tab keymap runs at document CAPTURE and stopPropagation()s the key, but it blurs the input first — handing the pick over on blur puts it in the row model before the deferred cell save diffs that row, which is what stops a Tab-out sending `input:{}`.
            onChange?.(e, transform(pendingOption), 'selectOption');
          }
          setFocused(false);

          /**
           * Closing here is what made Tab commit the wrong option.
           *
           *   arrow to "Pallets" ─▶ re-parent blur ─▶ listbox CLOSES
           *     ─▶ reopens with the value's own label still in the box, so the
           *        list filters down to that one option and `autoHighlight`
           *        snaps back to it ─▶ a Tab landing in that window commits
           *        "Units", the value the user had just arrowed AWAY from.
           *
           * While the grid still says this cell is the one being edited, the blur
           * is the editor being re-parented, not the user leaving. Staying open
           * means there is no reopen, no re-filter and no snap-back. On a real
           * departure this editor unmounts, which closes it anyway.
           */
          if (!hasFocus) {
            setOpen(false);
          }

          onBlur?.(e);
        }}
        renderInput={
          renderInput
            ? renderInput
            : (params) => (
                <TextFieldComponent
                  {...params}
                  {...textFieldProps}
                  inputRef={mergedInputRef}
                  label={label}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      sx: {
                        opacity: val === suggestedValue && !value && !focused ? 0.5 : 1,
                        ['& .MuiAutocomplete-endAdornment']: {
                          position: 'absolute',
                          right: 8, // Adjust based on padding or border widths
                          top: '50%',
                          transform: 'translateY(-50%)',
                        },
                      },
                      endAdornment: showResultsList ? (
                        <>
                          {endAdornment}
                          {params.slotProps.input.endAdornment}
                        </>
                      ) : (
                        <>
                          {loading && <CircularProgress color="inherit" size={20} />}
                          {params.slotProps.input.endAdornment}
                          {endAdornment}
                        </>
                      ),
                      ...(showResultsList
                        ? { startAdornment: startAdornment || params.slotProps.input.startAdornment }
                        : {}),
                      ...(textFieldProps?.slotProps?.input ?? {}),
                    },
                  }}
                />
              )
        }
        onOpen={(e) => {
          if (disabled) {
            return;
          }

          // prevent opening on focus
          if (!focused && e.currentTarget.tagName === 'INPUT' && !autoExpand) {
            setOpen(false);

            return;
          }

          setOpen(true);
          onOpen?.(e);
        }}
        onClose={(...e) => {
          const [, reason] = e;

          // Sibling of the blur branch above: MUI asks to close on the re-parent
          // blur too, and honouring it reopens onto a re-filtered list.
          if (!(hasFocus && reason === 'blur')) {
            setOpen(false);
          }

          onClose?.(...e);
        }}
        open={isOpen}
        onHighlightChange={(_, option) => {
          userChosenHighlight.current = option as ValueType | null;
          setHighlightedOption(option);
        }}
        onChange={(e, value, reason, details) => {
          const transformed = value instanceof Array ? value.map(transform) : transform(value);
          return onChange(e, transformed, reason, details);
        }}
        onKeyDown={(e) => {
          // Both Tab directions COMMIT what the user is looking at. Where focus lands
          // afterwards belongs to the grid's keymap; this only decides whether the
          // pick is handed over on the way out.
          if (e.key === 'Tab' && highlightedOption && isOpen) {
            const transformedOption = transform(highlightedOption);

            const hasHighlightedOption = props.options.some((option) => option === highlightedOption);

            if (!hasHighlightedOption) {
              return;
            }

            if (multiple) {
              const curValue = Array.isArray(value) ? value : [value];
              const newValue = [...curValue, transformedOption];

              setInputValue('');

              onChange(e, newValue, 'selectOption', { option: transformedOption });

              e.preventDefault();
            } else if (inputValue || userChosenHighlight.current !== null) {
              // Tab on the highlighted single-select option:
              //   empty input, default pick ──► fall through, keep prediction ✓
              //   empty input, user ARROWED ──► commit highlighted option     ✓
              //   typed input               ──► commit highlighted option     ✓
              // An empty-input highlight the user never moved is just
              // autoHighlight's arbitrary first pick — committing it would clobber
              // the useCellPrediction suggestion on that keydown. One the user
              // arrowed to is a deliberate choice and must commit, so a fast
              // operator can arrow-then-Tab straight through a grid row.
              onChange(e, transformedOption, 'selectOption');
              setOpen(false);
            }
          }

          onKeyDown?.(e);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          // A caller that searches the server sees every raw MUI input event (e.g. PlaceCell, TAS-3660 P7 BZ77).
          onInputChange?.(event, newInputValue, reason);
          /**
           * While a grid cell is being edited and the box holds the user's LIVE filter,
           * nothing but the user rewrites it.
           *
           *   'input'                    the user typing            ──▶ always applied
           *   'blur'  + live filter      the popped-out editor's
           *                              re-parent blur, ~every 2s  ──▶ ignored
           *   no event + live filter     MUI syncing the box to a
           *                              value that just changed    ──▶ ignored
           *
           * Both ignored cases would otherwise replace a half-typed filter with a
           * value's label mid-word, which is what made typing look like it appended.
           * A pick the user actually makes resets with an event and `reason:
           * 'selectOption'`, so it is never caught here.
           */
          const rewritesLiveFilter = hasFocus && inputIsUserFilter && (!event || reason === 'blur');

          if (rewritesLiveFilter) {
            return;
          }

          if (!event) {
            setInputValue((newInputValue || defaultInputValue) ?? '');
            return;
          }

          // 'input' is the user typing; 'reset'/'clear' is MUI syncing the box to the
          // value. Only the former leaves a pending pick for `pendingHighlightedOption`.
          if (reason === 'input') {
            // Their own filter now decides the highlight, not the arrow key before it.
            userChosenHighlight.current = null;
          }

          setInputIsUserFilter(reason === 'input');
          setInputValue(newInputValue);
        }}
        renderOption={renderOption}
      />
      {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
    </Box>
  );
}
